"use client";

// Formularios y filtros del tema dopamina. Solo lectura.
import Image from "next/image";
import { Camera, Search } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { isPlaceholder } from "@/dropai/legal";
import type { FaqEntry, Review, SalesContent } from "../types";
import { Stars } from "./parts";
import { FOCUS, PENDING, PRIMARY } from "./ui";

const FIELD = "mt-1.5 block min-h-12 w-full rounded-[14px] border border-(--wl-line) bg-(--wl-surface) px-4 text-base text-(--wl-ink) placeholder:text-(--wl-ink-muted) focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--wl-accent)";
const LABEL = "block text-sm font-semibold";

function mailto(to: string, subject: string, body: string): string {
  return "mailto:" + to + "?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(body);
}

/** Valida con los mensajes del navegador pero escritos aquí, en español y junto al campo. */
function useCheck() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  function check(form: HTMLFormElement): boolean {
    const next: Record<string, string> = {};
    for (const element of Array.from(form.elements)) {
      if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) || element.validity.valid) continue;
      next[element.name] = element.validity.valueMissing ? "Rellena este campo." : element.type === "email" ? "Escribe un correo válido, como nombre@correo.com." : "Revisa este campo.";
    }
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) (form.elements.namedItem(first) as HTMLElement | null)?.focus();
    return first === undefined;
  }
  return { errors, check };
}

function Error({ id, text }: { id: string; text?: string }) {
  return text ? <p id={id} className="mt-1.5 text-sm font-semibold text-(--wl-urgency)">{text}</p> : null;
}

export function NewsletterForm({ email }: { email: string }) {
  const id = useId();
  const [done, setDone] = useState(false);
  const { errors, check } = useCheck();
  if (isPlaceholder(email)) return <p className={PENDING + " mt-3"}>Pendiente: el correo de la tienda para el boletín</p>;
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!check(event.currentTarget)) return;
    const value = String(new FormData(event.currentTarget).get("correo") ?? "");
    window.location.href = mailto(email, "Alta en el boletín", "Quiero recibir las novedades y ofertas en " + value + ".");
    setDone(true);
  }
  return (
    <form noValidate onSubmit={submit} action={"mailto:" + email} className="mt-3">
      <label htmlFor={id} className="sr-only">Tu correo</label>
      <div className="flex gap-2">
        <input id={id} name="correo" type="email" required autoComplete="email" placeholder="tu@correo.com" aria-describedby={errors.correo ? id + "-e" : undefined} className={FIELD + " mt-0 min-w-0 flex-1 rounded-full bg-white/95 text-[#14121f]"} />
        <button type="submit" className={"min-h-12 shrink-0 rounded-full bg-(--wl-surface) px-5 font-bold text-(--wl-ink) transition-transform active:scale-95 " + FOCUS}>Apuntarme</button>
      </div>
      <Error id={id + "-e"} text={errors.correo} />
      <p className="mt-2 text-xs opacity-75">{done ? "Te hemos abierto el correo para enviarlo." : "Sin casillas marcadas: te das de baja cuando quieras."}</p>
    </form>
  );
}

/** Contacto (5.1): nombre, correo y mensaje, que llegan al correo del vendedor. */
export function ContactForm({ email, subject = "Consulta desde la tienda", order = false }: { email: string; subject?: string; order?: boolean }) {
  const id = useId();
  const [sent, setSent] = useState(false);
  const { errors, check } = useCheck();
  if (isPlaceholder(email)) return <p className={PENDING}>Pendiente: el correo de contacto de la tienda</p>;
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!check(event.currentTarget)) return;
    const data = new FormData(event.currentTarget);
    const field = (name: string) => String(data.get(name) ?? "").trim();
    const body = order
      ? "Número de pedido: " + field("pedido") + "\nCorreo del pedido: " + field("correo") + "\n\n¿En qué punto está mi pedido?"
      : field("mensaje") + "\n\n" + field("nombre") + " (" + field("correo") + ")";
    window.location.href = mailto(email, order ? "Seguimiento del pedido " + field("pedido") : subject, body);
    setSent(true);
  }
  return (
    <form noValidate onSubmit={submit} action={"mailto:" + email} className="grid gap-5">
      {order ? (
        <div>
          <label htmlFor={id + "-p"} className={LABEL}>Número de pedido</label>
          <input id={id + "-p"} name="pedido" required inputMode="text" autoComplete="off" placeholder="Lo tienes en el correo de confirmación" aria-describedby={errors.pedido ? id + "-pe" : undefined} className={FIELD} />
          <Error id={id + "-pe"} text={errors.pedido} />
        </div>
      ) : (
        <div>
          <label htmlFor={id + "-n"} className={LABEL}>Nombre</label>
          <input id={id + "-n"} name="nombre" required autoComplete="name" aria-describedby={errors.nombre ? id + "-ne" : undefined} className={FIELD} />
          <Error id={id + "-ne"} text={errors.nombre} />
        </div>
      )}
      <div>
        <label htmlFor={id + "-c"} className={LABEL}>{order ? "Correo del pedido" : "Correo"}</label>
        <input id={id + "-c"} name="correo" type="email" required autoComplete="email" aria-describedby={errors.correo ? id + "-ce" : undefined} className={FIELD} />
        <Error id={id + "-ce"} text={errors.correo} />
      </div>
      {order ? null : (
        <div>
          <label htmlFor={id + "-m"} className={LABEL}>Mensaje</label>
          <textarea id={id + "-m"} name="mensaje" required rows={5} aria-describedby={errors.mensaje ? id + "-me" : undefined} className={FIELD + " py-3"} />
          <Error id={id + "-me"} text={errors.mensaje} />
        </div>
      )}
      <button type="submit" className={PRIMARY + " w-full sm:w-auto"}>{order ? "VER MI PEDIDO" : "ENVIAR MENSAJE"}</button>
      <p role="status" className="text-sm text-(--wl-ink-muted)">{sent ? "Te hemos abierto el correo con el mensaje escrito: solo falta enviarlo." : "Se abre tu correo con el mensaje ya escrito para " + email + "."}</p>
    </form>
  );
}

const CATEGORY: Record<FaqEntry["cat"], string> = { pedido: "Pedido", envio: "Envío", producto: "Producto", pagos: "Pagos" };
const ORDER: FaqEntry["cat"][] = ["producto", "envio", "pagos", "pedido"];

/** /preguntas: por categorías, desplegables y con buscador. */
export function FaqBrowser({ faq }: { faq: FaqEntry[] }) {
  const id = useId();
  const [query, setQuery] = useState("");
  const words = query.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").split(/\s+/).filter(Boolean);
  const match = (item: FaqEntry) => {
    const text = (item.q + " " + item.a).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return words.every((word) => text.includes(word));
  };
  const shown = faq.filter(match);
  return (
    <div>
      <div className="relative max-w-[36rem]">
        <label htmlFor={id} className="sr-only">Buscar una pregunta</label>
        <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-(--wl-ink-muted)" />
        <input id={id} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Busca: envío, talla, devolver…" className={FIELD + " mt-0 rounded-full pl-12 shadow-(--wl-shadow-soft)"} />
      </div>
      <nav aria-label="Categorías" className="mt-6 flex flex-wrap gap-2">
        {ORDER.filter((cat) => faq.some((item) => item.cat === cat)).map((cat) => (
          <a key={cat} href={"#" + cat} className={"wl-glass inline-flex min-h-11 items-center rounded-full px-4 text-sm font-bold " + FOCUS}>{CATEGORY[cat]}</a>
        ))}
      </nav>
      {shown.length === 0 ? <p role="status" className="mt-10 text-lg font-semibold">Ninguna pregunta habla de eso. Escríbenos desde Contacto y te respondemos.</p> : null}
      <div className="mt-8 grid gap-10">
        {ORDER.map((cat) => {
          const items = shown.filter((item) => item.cat === cat);
          if (items.length === 0) return null;
          return (
            <section key={cat} id={cat} aria-labelledby={cat + "-t"} className="scroll-mt-28">
              <h2 id={cat + "-t"} className="font-display text-2xl [font-weight:var(--wl-display-weight)] lg:text-3xl">{CATEGORY[cat]}</h2>
              <div className="mt-4 grid gap-3">
                {items.map((item) => (
                  <details key={item.q} className="group rounded-[16px] bg-(--wl-surface) px-5 shadow-(--wl-shadow-soft) open:pb-4">
                    <summary className={"flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 py-3 font-bold [&::-webkit-details-marker]:hidden " + FOCUS}>
                      {item.q}
                      <span aria-hidden="true" className="wl-grad-accent flex size-7 shrink-0 items-center justify-center rounded-full text-lg leading-none transition-transform duration-300 group-open:rotate-45">+</span>
                    </summary>
                    <p className="leading-relaxed text-pretty">{item.a}</p>
                  </details>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

const DATE = new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Madrid" });

export function ReviewCard({ review }: { review: Review }) {
  return (
    <article className="flex h-full flex-col gap-3 rounded-[20px] bg-(--wl-surface) p-5 text-(--wl-ink) shadow-(--wl-shadow-soft)">
      <div className="flex items-center justify-between gap-3">
        <Stars rating={review.rating} />
        <time dateTime={review.date} className="text-xs text-(--wl-ink-muted)">{DATE.format(new Date(review.date + "T12:00:00Z"))}</time>
      </div>
      <h3 className="font-bold">{review.title}</h3>
      <p className="leading-relaxed text-pretty">{review.text}</p>
      {review.photo ? (
        <div className="relative mt-1 aspect-square w-24 overflow-hidden rounded-[12px]">
          <Image src={review.photo} alt={"Foto de la opinión de " + review.name} fill sizes="96px" className="object-cover" />
        </div>
      ) : null}
      <p className="mt-auto text-sm font-semibold">{review.name}</p>
    </article>
  );
}

/** /opiniones: filtros por estrellas y por fotos, y las fotos de clientes. */
export function ReviewsBrowser({ proof }: { proof: NonNullable<SalesContent["proof"]> }) {
  const [stars, setStars] = useState<number | null>(null);
  const [photos, setPhotos] = useState(false);
  const shown = proof.reviews.filter((review) => (stars === null || review.rating === stars) && (!photos || review.photo !== undefined));
  const chip = (on: boolean) => "inline-flex min-h-11 items-center gap-1.5 rounded-full px-4 text-sm font-bold transition-colors " + (on ? "wl-grad-accent" : "wl-glass") + " " + FOCUS;
  const withPhoto = proof.reviews.filter((review) => review.photo !== undefined);
  return (
    <div>
      {withPhoto.length > 0 ? (
        <div className="mb-10">
          <h2 className="flex items-center gap-2 text-lg font-bold"><Camera aria-hidden="true" className="size-5" />Fotos de clientes</h2>
          <ul className="mt-4 flex snap-x gap-3 overflow-x-auto pb-2">
            {withPhoto.map((review, index) => (
              <li key={review.name + String(index)} className="relative aspect-square w-32 shrink-0 snap-start overflow-hidden rounded-[16px] lg:w-40">
                <Image src={review.photo!} alt={"Foto de " + review.name} fill sizes="160px" className="object-cover" />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div role="group" aria-label="Filtrar opiniones" className="flex flex-wrap gap-2">
        <button type="button" aria-pressed={stars === null && !photos} onClick={() => { setStars(null); setPhotos(false); }} className={chip(stars === null && !photos)}>Todas</button>
        {[5, 4, 3, 2, 1].filter((value) => proof.distribution[5 - value] > 0).map((value) => (
          <button key={value} type="button" aria-pressed={stars === value} onClick={() => setStars(stars === value ? null : value)} className={chip(stars === value)}>
            {String(value) + " ★ (" + String(proof.distribution[5 - value]) + ")"}
          </button>
        ))}
        {withPhoto.length > 0 ? <button type="button" aria-pressed={photos} onClick={() => setPhotos(!photos)} className={chip(photos)}>Con foto</button> : null}
      </div>
      <p role="status" className="mt-4 text-sm text-(--wl-ink-muted)">{String(shown.length) + (shown.length === 1 ? " opinión" : " opiniones")}</p>
      <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((review, index) => (
          <li key={review.name + review.date + String(index)}><ReviewCard review={review} /></li>
        ))}
      </ul>
    </div>
  );
}
